import type { Driver } from '@cobliteam/api-lib';
import { useDriver } from '@cobliteam/hooks';
import { SelectVirtualized } from '@cobliteam/ui/form';
import { DriverDrawer } from '@cobliteam/ui-business/components';
import { type PropsWithChildren, useMemo, useState } from 'react';
import NoDriverFound from './components/NoDriverFound';
import SelectDriverTrigger from './components/SelectDriverTrigger';
import SelectItemDriver from './components/SelectItemDriver';

export type DriverOption = {
  id: string;
  name: string;
  profileImageUrl?: string;
  fixed?: boolean;
};

interface SelectDriverProps {
  className?: string;
  value?: DriverOption;
  disabled?: boolean;
  height?: number;
  width?: number;
  allowNoDriver?: boolean;
  creatable?: boolean;
  confirmable?: boolean;
  onSuccessCreate?: (driver: Driver) => void;
  portal?: boolean;
  onChange: (driver?: DriverOption) => void;
  hasTriggerStartIcon?: boolean;
}

const NO_DRIVER_ID = 'no-driver';

const SelectDriver = ({
  children,
  className,
  value,
  allowNoDriver = false,
  creatable = false,
  confirmable = false,
  disabled = false,
  height = 200,
  width = 370,
  portal = true,
  onChange,
  hasTriggerStartIcon = true,
  onSuccessCreate,
}: PropsWithChildren<SelectDriverProps>) => {
  const [createDriverDialogOpen, setCreateDriverDialogOpen] = useState(false);

  const { getDrivers } = useDriver();

  const { data: drivers, isLoading: isLoadingDrivers } = getDrivers({
    options: {
      enabled: true,
    },
  });

  const options: DriverOption[] = useMemo(
    () => [
      ...(allowNoDriver
        ? [
            {
              id: NO_DRIVER_ID,
              name: 'Sem motorista',
              fixed: true,
            },
          ]
        : []),
      ...(drivers?.map(({ id, name }) => ({
        id,
        name,
      })) ?? []),
    ],
    [allowNoDriver, drivers]
  );

  const sortedOptions = useMemo(() => {
    return options
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(({ id, name, fixed, profileImageUrl }) => ({
        value: id,
        label: name,
        fixed,
        profileImageUrl,
      }));
  }, [options]);

  const changeDriver = (option: DriverOption) => {
    onChange(option.id !== NO_DRIVER_ID ? option : undefined);
  };

  const onDriverCreated = (createdDriver: Driver) => {
    onSuccessCreate?.(createdDriver);
    onChange({
      id: createdDriver.id,
      name: createdDriver.name,
    });
  };

  return (
    <>
      {!createDriverDialogOpen && (
        <SelectVirtualized
          options={sortedOptions}
          value={
            value && {
              value: value.id,
              label: value.name,
              fixed: value.fixed,
              profileImageUrl: value.profileImageUrl,
            }
          }
          height={height}
          popoverWidth={width}
          searchPlaceholder='Procure pelo nome'
          placeholder='Selecione um motorista'
          emptyIndicator={
            <NoDriverFound
              creatable={creatable}
              onCreateDriverClick={() => {
                setCreateDriverDialogOpen(true);
              }}
            />
          }
          disabled={isLoadingDrivers}
          onValueChange={(option) => {
            option &&
              changeDriver({
                id: option.value,
                name: option.label,
              });
          }}
          className={className}
          portal={portal}
          optionHeight={40}
          align='start'
          advancedSelectItem={({ option, isSelected }) => ({
            className: isSelected ? 'bg-gray-2' : '',
            children: <SelectItemDriver id={option.value} name={option.label} isSelected={isSelected} />,
          })}
          confirmable={confirmable}
        >
          <div>{children ? children : <SelectDriverTrigger value={value?.name || ''} disabled={disabled} withoutStartIcon={!hasTriggerStartIcon} />}</div>
        </SelectVirtualized>
      )}
      <DriverDrawer
        allDriversData={drivers || []}
        open={createDriverDialogOpen}
        onCloseDrawer={() => {
          setCreateDriverDialogOpen(false);
        }}
        isLoading={isLoadingDrivers}
        onSuccess={onDriverCreated}
      />
    </>
  );
};

export default SelectDriver;
